"use client";

import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { motion } from "framer-motion";
import { ReactNode, useState } from "react";
import { faqData } from "@/data/faq";
import { classNames } from "@/helpers/tailwind-class";

function Accordion({ title, content }: { title: string; content: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="group w-full cursor-pointer py-2" onClick={() => setIsOpen(!isOpen)}>
      <div className="flex items-center justify-start gap-2 py-2">
        <motion.div
          initial={false}
          animate={{ rotate: isOpen ? 90 : 0 }}
          transition={{ duration: 0.2 }}
          className={classNames(
            "btn btn-circle btn-outline btn-sm group-hover:text-gray-300",
            isOpen ? "text-gray-300" : "text-gray-500"
          )}
        >
          <ChevronRightIcon className="size-5" />
        </motion.div>
        <h3 className="text-base">{title}</h3>
      </div>
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.2 }}
        style={{ overflow: "hidden" }}
      >
        <div className="py-2 text-sm">{content}</div>
      </motion.div>
    </div>
  );
}

interface FaqSectionProps {
  title?: string;
  faqKey: keyof typeof faqData;
}

export function FaqSection({ title = "Frequently asked questions", faqKey }: FaqSectionProps) {
  return (
    <div className="mx-auto flex w-full max-w-(--breakpoint-md) flex-col items-center justify-center gap-4 rounded-lg border border-b-0 border-slate-700 bg-slate-800 px-4 shadow-lg sm:px-8">
      <h3 className="my-8 text-center text-2xl sm:text-4xl">{title}</h3>

      <div className="divide-y divide-slate-700">
        {faqData[faqKey].map((q) => {
          return <Accordion key={q.question} title={q.question} content={q.answer} />;
        })}
      </div>
    </div>
  );
}

interface FaqSectionWithFaqProps {
  title?: string;
  faqs: { question: string; answer: ReactNode }[];
}

export function FaqSectionWithFaq({
  title = "Frequently asked questions",
  faqs,
}: FaqSectionWithFaqProps) {
  return (
    <div className="mx-auto flex w-full max-w-(--breakpoint-md) flex-col items-center justify-center gap-4 rounded-lg border border-b-0 border-slate-700 bg-slate-800 px-4 shadow-lg sm:px-8">
      <h3 className="my-8 text-center text-2xl sm:text-4xl">{title}</h3>

      <div className="divide-y divide-slate-700">
        {faqs.map((q) => {
          return <Accordion key={q.question} title={q.question} content={q.answer} />;
        })}
      </div>
    </div>
  );
}
